import React, { useContext, useMemo, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FaHouse } from 'react-icons/fa6';
import { UserContext } from '../context/UserContext';
import './Breadcrumb.scss';

// Configuration constants
const showBreadcrumbOnlyInMobile = false;
const MAX_BREADCRUMB_ITEMS = 4; // Maximum number of items to show (including home)
const MAX_TEXT_LENGTH = 30; // Maximum characters for each breadcrumb text

/**
 * Get the page title from various sources in priority order:
 * 1. Element with data-page-title attribute
 * 2. Query parameter 'name'
 * 3. First header element (h1, h2, or h3)
 *
 * Returns an object with { title, shouldRetry }
 * shouldRetry is false if we found data-page-title or query param (no need to retry)
 */
const getPageTitle = (defaultTitle) => {
  // 1. Check for data-page-title attribute
  const pageTitleElement = document.querySelector('[data-page-title]');
  if (pageTitleElement) {
    const title = pageTitleElement.getAttribute('data-page-title');
    if (title) return { title, shouldRetry: false };
  }

  // 2. Check query parameter (no need to retry if found)
  const searchParams = new URLSearchParams(window.location.search);
  const nameParam = searchParams.get('name');
  if (nameParam) return { title: nameParam, shouldRetry: false };

  // 3. Check header elements (these might load later, so retry if not found)
  const h1 = document.querySelector('h1');
  if (h1 && h1.textContent.trim()) {
    return { title: h1.textContent.trim(), shouldRetry: false };
  }

  const h2 = document.querySelector('h2');
  if (h2 && h2.textContent.trim()) {
    return { title: h2.textContent.trim(), shouldRetry: false };
  }

  const h3 = document.querySelector('h3');
  if (h3 && h3.textContent.trim()) {
    return { title: h3.textContent.trim(), shouldRetry: false };
  }

  // No title found, should retry
  return { title: defaultTitle, shouldRetry: true };
};

/**
 * Truncate text if it exceeds MAX_TEXT_LENGTH
 */
const truncateText = (text, maxLength = MAX_TEXT_LENGTH) => {
  if (typeof text !== 'string') return text;
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

// Route configuration mapping paths to their Hebrew labels
const routeConfig = {
  '/homepage': 'דף הבית',
  '/protocols': 'פרוטוקולים',
  '/protocols/new': 'פרוטוקול חדש',
  '/hospitals': 'בתי חולים',
  '/antibiogram': 'ניהול אנטיביוגרם',
  '/settings': 'הגדרות אדמין',
  '/test-tubes': 'ניהול מבחנות מעבדה',
  '/resusAdmin': 'ניהול חדר הלם',
  '/resusAdmin/medications': 'ניהול מאגר תרופות',
  '/preemie-nutrition-config': 'ניהול תזונת פגים',
  '/nicu-nutrition-config': 'ניהול הזנה בטיפול נמרץ ילדים',
  '/phototherapy-config': 'ניהול מחשבון צהבת ביילוד',
  '/bili2022-config': 'ניהול מחשבון צהבת ביילוד AAP 2022',
  '/profile': 'פרופיל',
  '/users': 'משתמשים',
  '/users/new': 'משתמש חדש',
  '/training': 'אימון ובחינה',
  '/cases': 'מקרים רפואיים',
  '/cases/new': 'מקרה חדש',
  '/checklists': 'רשימות',
  '/checklists/templates/new': 'תבנית חדשה',
  '/department-documents': 'מסמכי המחלקה',
  '/apps-mgmt': 'ניהול אפליקציות',
  '/apps-mgmt/add': 'אפליקציה חדשה',
  '/main/apps': 'אפליקציות',
};

const Breadcrumb = () => {
  const location = useLocation();
  const params = useParams();
  const { user, hospitals } = useContext(UserContext);
  const [dynamicTitle, setDynamicTitle] = useState(null);
  const [dynamicSubtitle, setDynamicSubtitle] = useState(null);

  // Update dynamic title when DOM changes with retry mechanism
  useEffect(() => {
    setDynamicSubtitle(null);
    let attempts = 0;
    const maxAttempts = 20; // 20 attempts * 100ms = 2 seconds max
    const retryInterval = 100; // Check every 100ms
    let intervalId;

    const checkForTitle = () => {
      attempts++;
      const result = getPageTitle(null);

      // If we found a title from data-page-title, query param, or header elements
      if (result.title) {
        setDynamicTitle(result.title);
        if (intervalId) clearInterval(intervalId);
        return true;
      }

      // If we shouldn't retry (e.g., query param or data-page-title exists but empty)
      // or we've reached max attempts, stop trying
      if (!result.shouldRetry || attempts >= maxAttempts) {
        if (intervalId) clearInterval(intervalId);
        setDynamicTitle(null); // Set to null to use fallback
        return false;
      }

      return false;
    };

    // Initial check after small delay
    const initialTimer = setTimeout(() => {
      const found = checkForTitle();

      // If title wasn't found on first check, set up interval to keep trying
      if (!found) {
        intervalId = setInterval(checkForTitle, retryInterval);
      }
    }, 100);

    return () => {
      clearTimeout(initialTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [location.pathname, location.search]);

  // Watch for data-page-title attribute changes (e.g. in-app navigation)
  useEffect(() => {
    let rafId;
    const observer = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some(
        (m) => m.type === 'attributes' ||
        (m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0))
      );
      if (!hasRelevantChange) return;

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const result = getPageTitle(null);
        if (result.title) {
          setDynamicTitle((prev) => prev !== result.title ? result.title : prev);
        }
        const subEl = document.querySelector('[data-page-subtitle]');
        const sub = subEl ? subEl.getAttribute('data-page-subtitle') : null;
        setDynamicSubtitle((prev) => prev !== sub ? sub : prev);
      });
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-page-title', 'data-page-subtitle'],
    });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname]);

  const breadcrumbItems = useMemo(() => {
    const items = [];
    const homepage = '/homepage';

    // Add home icon
    items.push({
      label: <FaHouse />,
      path: homepage,
      isHome: true
    });

    const pathname = location.pathname;

    // Handle special cases first

    // Apps routes - /main/:hospitalName/:appName or /main/:hospitalName
    const mainAppsMatch = pathname.match(/^\/main\/([^/]+)(\/([^/]+))?/);
    if (mainAppsMatch) {
      items.push({
        label: 'אפליקציות',
        path: '/main/apps'
      });

      if (mainAppsMatch[3]) {
        if (dynamicSubtitle) {
          // Hierarchical: show app title as intermediate, subtitle as last
          const appTitle = dynamicTitle || decodeURIComponent(mainAppsMatch[3]);
          items.push({
            label: truncateText(appTitle),
            path: pathname,
            onClick: () => window.dispatchEvent(new CustomEvent('breadcrumb-navigate-parent')),
          });
          items.push({
            label: truncateText(dynamicSubtitle),
            path: pathname,
            isLast: true
          });
        } else {
          // Has app name - use dynamic title or fallback
          const appTitle = dynamicTitle || decodeURIComponent(mainAppsMatch[3]);
          items.push({
            label: truncateText(appTitle),
            path: pathname,
            isLast: true
          });
        }
      }

      return items;
    }

    // Protocol routes
    if (pathname.startsWith('/protocols/')) {
      items.push({
        label: 'פרוטוקולים',
        path: '/protocols'
      });

      const protocolId = params.id;
      if (protocolId && protocolId !== 'new') {
        // Use dynamic title or fallback
        const protocolName = dynamicTitle || 'פרוטוקול';

        items.push({
          label: truncateText(protocolName),
          path: pathname,
          isLast: true
        });
      } else if (pathname.includes('/new')) {
        items.push({
          label: 'פרוטוקול חדש',
          path: pathname,
          isLast: true
        });
      }

      return items;
    }

    // Medical cases routes
    if (pathname.startsWith('/cases/')) {
      items.push({
        label: 'מקרים רפואיים',
        path: '/cases'
      });

      const caseId = params.id;
      if (caseId && caseId !== 'new') {
        const caseName = dynamicTitle || 'מקרה רפואי';

        items.push({
          label: truncateText(caseName),
          path: pathname,
          isLast: true
        });
      } else if (pathname.includes('/new')) {
        items.push({
          label: 'מקרה חדש',
          path: pathname,
          isLast: true
        });
      }

      return items;
    }

    // Checklists routes
    if (pathname.startsWith('/checklists/')) {
      items.push({
        label: 'רשימות',
        path: '/checklists'
      });

      if (pathname.includes('/templates/new')) {
        items.push({
          label: 'תבנית חדשה',
          path: pathname,
          isLast: true
        });
      } else if (pathname.includes('/templates/view/') || pathname.includes('/templates/edit/')) {
        const templateName = dynamicTitle || 'תבנית';

        items.push({
          label: truncateText(templateName),
          path: pathname,
          isLast: true
        });
      } else if (pathname.includes('/instance/')) {
        const instanceName = dynamicTitle || 'רשימה';

        items.push({
          label: truncateText(instanceName),
          path: pathname,
          isLast: true
        });
      }

      return items;
    }

    // Department documents: /department-documents/:id shows one file
    if (pathname.startsWith('/department-documents/')) {
      items.push({ label: 'מסמכי המחלקה', path: '/department-documents' });
      items.push({ label: truncateText(dynamicTitle || 'מסמך'), path: pathname, isLast: true });
      return items;
    }

    // Users routes
    if (pathname.startsWith('/users/')) {
      items.push({
        label: 'משתמשים',
        path: '/users'
      });

      const userId = params.id;
      if (userId) {
        const userName = dynamicTitle || 'משתמש';

        items.push({
          label: truncateText(userName),
          path: pathname,
          isLast: true
        });
      } else if (pathname.includes('/new')) {
        items.push({
          label: 'משתמש חדש',
          path: pathname,
          isLast: true
        });
      }

      return items;
    }

    // Apps management routes
    if (pathname.startsWith('/apps-mgmt/')) {
      items.push({
        label: 'ניהול אפליקציות',
        path: '/apps-mgmt'
      });

      if (pathname.includes('/add')) {
        items.push({
          label: 'אפליקציה חדשה',
          path: pathname,
          isLast: true
        });
      } else if (pathname.includes('/edit/')) {
        const appName = dynamicTitle || 'אפליקציה';

        items.push({
          label: truncateText(appName),
          path: pathname,
          isLast: true
        });
      }

      return items;
    }

    // Simple routes - just add the single level
    const simpleRouteLabel = routeConfig[pathname];
    if (simpleRouteLabel) {
      items.push({
        label: simpleRouteLabel,
        path: pathname,
        isLast: true
      });
    }

    return items;
  }, [location.pathname, location.search, params, user, hospitals, dynamicTitle, dynamicSubtitle]);

  // Limit breadcrumb items if too many
  let displayItems = breadcrumbItems;
  if (breadcrumbItems.length > MAX_BREADCRUMB_ITEMS) {
    // Keep home, add ellipsis, keep last items
    const itemsToKeep = MAX_BREADCRUMB_ITEMS - 2; // -2 for home and ellipsis
    displayItems = [
      breadcrumbItems[0], // Home
      { label: '...', path: null, isEllipsis: true },
      ...breadcrumbItems.slice(-itemsToKeep)
    ];
  }

  // Don't show breadcrumb if only home icon or if we're on homepage
  if (breadcrumbItems.length <= 1) {
    return null;
  }

  const ChevronSeparator = () => (
    <span className="breadcrumb-separator">
      <svg width="9" height="10" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.5 14.5L1.5 8L7.5 1.5" stroke="#9CACBA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );

  return (
    <div className={`app-breadcrumb ${showBreadcrumbOnlyInMobile ? 'mobile-only' : ''}`}>
      {displayItems.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronSeparator />}
          {item.isEllipsis ? (
            <span className="breadcrumb-ellipsis">{item.label}</span>
          ): (
            <Link
              to={item.path}
              className="breadcrumb-link"
              onClick={item.onClick ? (e) => { e.preventDefault(); item.onClick(); } : undefined}
            >
              {item.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Breadcrumb;
